import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useIntl } from 'react-intl';

import { formatMoney } from '../../../util/currency';
import { nightsBetween } from '../../../util/dates';
import { FormattedMessage } from '../../../util/reactIntl';
import {
  COMMISSION_LINE_ITEMS,
  EXTRA_LINE_ITEMS,
  LINE_ITEM_LISTING_CONTINGENT,
  LINE_ITEM_NIGHT,
  NEGATIVE_LINE_ITEMS,
} from '../../../util/types';
import { itemValueStyles, lineItemStyles, totalDividerStyles } from './BookingBreakdown.styles';
import { createExtraLineItemLabel } from './extraLineItemLabel';
import {
  EMPTY_CUSTOM_LINE_ITEM_TRANSLATIONS,
  resolveCustomLineItemTitle,
} from './customLineItemTranslations';

const createCustomLineItemLabel = (
  item,
  intl,
  quantity,
  seats,
  units,
  nights,
  guestCount,
  customLineItems,
  customLineItemTranslations
) => {
  const customLineItem = customLineItems.find((cli) => cli.code === item.code);
  if (!customLineItem) return '';

  const title = resolveCustomLineItemTitle(
    item.code,
    customLineItem,
    customLineItemTranslations,
    intl.locale
  );

  const unitPrice = formatMoney(intl, item.unitPrice);

  if (quantity < 0) {
    quantity = quantity * -1;
  }
  if (quantity && !seats && !units && title) {
    return title
      ? `${unitPrice} * ${quantity} ${title}`
      : `${unitPrice} * ${quantity} ${item.code}`;
  } else if (!(quantity && !seats && !units) && title) {
    return title ? `${unitPrice} ${title}` : `${unitPrice} ${item.code}`;
  }
  return intl.formatMessage(
    { id: `BookingBreakdown.${item.code.replace('line-item/', '')}` },
    { unitPrice, units, seats }
  );
};

const createQuantityBreakdown = (item, intl, nights, guestCount, customLineItems) => {
  const customLineItem = customLineItems.find((cli) => cli.code === item.code);
  if (!customLineItem) return '';

  const { type } = customLineItem || {};
  let quantityBreakdown = '';
  if (type?.perNight) {
    quantityBreakdown += intl.formatMessage(
      { id: 'LineItemUnknownItemsMaybe.perNight' },
      { count: nights }
    );
  }
  if (type?.perGuest && type?.perNight) {
    quantityBreakdown += intl.formatMessage({
      id: 'LineItemUnknownItemsMaybe.and',
    });
  }
  if (type?.perGuest) {
    quantityBreakdown += intl.formatMessage(
      { id: 'LineItemUnknownItemsMaybe.perGuest' },
      { count: guestCount }
    );
  }
  return <Typography component="span" variant="lineItemMuted">{quantityBreakdown}</Typography>;
};

/**
 * @param {object} props - component props
 * @param {object} props.transaction - transaction object
 * @param {object} [props.customLineItemTranslations] - Storyblok custom line item
 *   translations; absent for listings that have none
 * @param {'provider' | 'customer'} props.userRole - user role
 */

const LineItemUnknownRefundMaybe = ({
  transaction,
  userRole,
  customLineItemTranslations = EMPTY_CUSTOM_LINE_ITEM_TRANSLATIONS,
}) => {
  const intl = useIntl();

  let items =
    userRole === 'provider'
      ? transaction.attributes.lineItems.filter((item) => item.includeFor.includes('provider'))
      : transaction.attributes.lineItems.filter((item) => item.includeFor.includes('customer'));

  // Remove the night line item if it's not a refund
  items = items.filter((item) => item.code !== LINE_ITEM_NIGHT && item.lineTotal.amount < 0);

  // Remove vouchers as they are negative
  items = items.filter((item) => !NEGATIVE_LINE_ITEMS.includes(item.code));

  // Remove all comission line items
  items = items.filter((item) => !COMMISSION_LINE_ITEMS.includes(item.code));

  // Remove listing contingent line items
  items = items.filter((item) => item.code !== LINE_ITEM_LISTING_CONTINGENT);

  // Remove unknown line items
  items = items.filter((item) => !EXTRA_LINE_ITEMS.includes(item.code));

  const { start, end } = transaction.booking.attributes;
  const nights = nightsBetween(start, end);
  // guestCount may be missing entirely: legacy bookings only carry it as a plain
  // number, and metadata restored from a transaction (rather than fresh booking
  // data) doesn't always include it. Same tolerant pattern as CheckoutPage and
  // LineItemUnknownItemsMaybe — Object.entries(undefined) would otherwise throw.
  const { guestCount: rawGuestCount } = transaction.attributes.metadata;
  const guestCount =
    typeof rawGuestCount === 'number'
      ? rawGuestCount
      : rawGuestCount
        ? Object.entries(rawGuestCount).reduce((acc, [key, value]) => {
            if (typeof value === 'number') {
              return acc + value;
            }
            return acc;
          }, 0)
        : null;
  const customLineItems = transaction.attributes.metadata.customLineItems || [];
  const extraLineItems = transaction.attributes.metadata.extraLineItems || [];

  if (items.length === 0) return null;

  const renderExtraLineItems = (item, index) => {
    const quantity = item.quantity;
    const seats = item.seats;
    const units = item.units;

    const label = EXTRA_LINE_ITEMS.includes(item.code)
      ? createExtraLineItemLabel(item, intl, quantity, seats, units, nights, extraLineItems)
      : createCustomLineItemLabel(
          item,
          intl,
          quantity,
          seats,
          units,
          nights,
          guestCount,
          customLineItems,
          customLineItemTranslations
        );

    const quantityBreakdown = createQuantityBreakdown(
      item,
      intl,
      nights,
      guestCount,
      customLineItems
    );

    const formattedTotal = formatMoney(intl, item.lineTotal);
    const refundLabel = intl.formatMessage({ id: 'BookingBreakdown.refund' });

    return (
      <Box key={`${index}-item.code`} sx={lineItemStyles}>
        <Typography sx={{ flex: 1 }} variant="lineItemLabel">
          {label + ' ' + refundLabel}
          <br />
          {quantityBreakdown}
        </Typography>
        <Typography sx={itemValueStyles} variant="lineItemLabel">{formattedTotal}</Typography>
      </Box>
    );
  };

  return (
    <>
      <Divider sx={totalDividerStyles} />
      <Typography fontWeight="bold" variant="lineItemLabel">
        <FormattedMessage id="BookingBreakdown.additionalOffer.refund" />
      </Typography>
      {items.map((item, i) => renderExtraLineItems(item, i))}
    </>
  );
};

export default LineItemUnknownRefundMaybe;
